/**
 * Settings UI primitives — small, reusable building blocks shared across every
 * settings section. Kept in one file so a section only needs a single import.
 *
 * Visual language: soft shadows, rounded-[1.75rem] cards, generous spacing,
 * subtle motion — a modern SaaS dashboard aesthetic.
 */
import React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { Icons } from '@/src/modules/components/Icons';

/* ------------------------------------------------------------------ Card -- */

export function SettingsCard({
  title,
  description,
  icon: Icon,
  action,
  className,
  children,
}: {
  title?: string;
  description?: string;
  icon?: any;
  action?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className={cn(
        'bg-surface-lowest rounded-[1.75rem] border border-outline-variant/30 shadow-sm',
        className,
      )}
    >
      {(title || action) && (
        <header className="flex items-start justify-between gap-4 px-6 md:px-8 pt-6 md:pt-7">
          <div className="flex items-start gap-3">
            {Icon && (
              <span className="mt-0.5 w-9 h-9 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Icon size={18} />
              </span>
            )}
            <div>
              {title && <h3 className="text-base font-bold text-on-surface">{title}</h3>}
              {description && (
                <p className="text-sm text-on-surface-variant mt-0.5">{description}</p>
              )}
            </div>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      <div className="px-6 md:px-8 py-6">{children}</div>
    </motion.section>
  );
}

/* ----------------------------------------------------------------- Rows -- */

/** A labelled row — description on the left, a control on the right. */
export function SettingRow({
  label,
  description,
  htmlFor,
  children,
  className,
}: {
  label: string;
  description?: string;
  htmlFor?: string;
  children?: React.ReactNode;
  className?: string;
  /** This project ships without `@types/react`, so `key` must be declared. */
  key?: React.Key;
}) {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4 first:pt-0 last:pb-0 border-b border-outline-variant/15 last:border-0',
        className,
      )}
    >
      <div className="pr-4">
        <label htmlFor={htmlFor} className="text-sm font-semibold text-on-surface">
          {label}
        </label>
        {description && (
          <p className="text-xs text-on-surface-variant mt-0.5 max-w-md">{description}</p>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/* -------------------------------------------------------------- Toggle -- */

export function ToggleSwitch({
  checked,
  onChange,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'bg-primary' : 'bg-outline-variant/60',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 600, damping: 32 }}
        className={cn(
          'h-5 w-5 rounded-full bg-white shadow-md',
          checked ? 'ml-auto mr-0.5' : 'ml-0.5',
        )}
      />
    </button>
  );
}

/* --------------------------------------------------------------- Field -- */

export function TextField({
  label,
  id,
  error,
  hint,
  icon: Icon,
  className,
  ...props
}: {
  label?: string;
  id?: string;
  error?: string;
  hint?: string;
  icon?: any;
  className?: string;
  [prop: string]: unknown;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-xs font-semibold text-on-surface-variant">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
          />
        )}
        <input
          id={id}
          className={cn(
            'w-full rounded-xl border bg-surface-low/60 px-3.5 py-2.5 text-sm text-on-surface',
            'placeholder:text-on-surface-variant/60 transition-all outline-none',
            'focus:bg-surface-lowest focus:ring-2 focus:ring-primary/30 focus:border-primary',
            Icon && 'pl-9',
            error ? 'border-status-failed' : 'border-outline-variant/50',
          )}
          {...props}
        />
      </div>
      {error ? (
        <span className="text-xs text-status-failed font-medium">{error}</span>
      ) : (
        hint && <span className="text-xs text-on-surface-variant">{hint}</span>
      )}
    </div>
  );
}

export function SelectField({
  label,
  id,
  options,
  className,
  ...props
}: {
  label?: string;
  id?: string;
  options: { value: string; label: string }[];
  className?: string;
  [prop: string]: unknown;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-xs font-semibold text-on-surface-variant">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          className={cn(
            'w-full appearance-none rounded-xl border border-outline-variant/50 bg-surface-low/60 px-3.5 py-2.5 pr-9',
            'text-sm text-on-surface transition-all outline-none cursor-pointer',
            'focus:bg-surface-lowest focus:ring-2 focus:ring-primary/30 focus:border-primary',
          )}
          {...props}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Icons.ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Skeleton -- */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('animate-pulse rounded-lg bg-outline-variant/25', className)} />
  );
}

/* --------------------------------------------------------------- Pill --- */

/** A small status pill, e.g. "Connected" / "Recommended". */
export function Pill({
  children,
  tone = 'neutral',
  className,
}: {
  children?: React.ReactNode;
  tone?: 'neutral' | 'success' | 'primary' | 'warning';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-surface-low text-on-surface-variant',
    success: 'bg-status-published/10 text-status-published',
    primary: 'bg-primary/10 text-primary',
    warning: 'bg-status-processing/10 text-status-processing',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
